import { useEffect, useRef, useState } from 'react';
import { telegramMiniAppAuth } from '@/api/auth';
import { useTwa } from '@/app/TwaProvider';
import { Spinner } from '@/components/Spinner';
import { lastSessionEndReason, useAuthStore, useToastStore } from '@/store/auth';
import { loadLoginNotifications } from '@/store/loginNotifications';
import { getReferralCode } from '@/lib/referral';
import { hasTelegramLaunchPayload } from '@/lib/tgLaunch';
import { mapAuthError } from '@/lib/authErrors';
import { useT } from '@/i18n/useT';

/**
 * Signs a Mini App visitor in automatically on first open.
 *
 * Same call the "TG Quick Sign In" button makes — POST the signed initData to
 * the miniapp-auth endpoint, which signs existing users in and auto-creates
 * first-timers — just triggered by opening the app instead of by a tap.
 *
 * Runs at most once per page load. A failure is non-fatal: the attempt is
 * marked done, the user lands on the normal logged-out UI, and the manual
 * button is still there.
 */
function isInstalledPwa(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches === true
    || (navigator as { standalone?: boolean }).standalone === true;
}

export function useTelegramAutoSignIn(): { pending: boolean } {
  const { ready, detecting, initData, isTelegram, user, getLiveInitData } = useTwa();
  const isAuthed = useAuthStore((s) => s.isAuthed());
  const setSession = useAuthStore((s) => s.setSession);
  const showError = useToastStore((s) => s.showError);
  const t = useT('login');
  const [pending, setPending] = useState(false);
  // The cold open, plus one recovery from a token the backend rejected.
  // Capped because a backend that rejects every token would otherwise have
  // this mint a new session against it forever.
  const MAX_ATTEMPTS = 2;
  const attempts = useRef(0);
  const inFlight = useRef(false);
  // Whether the question "should this visitor be signed in?" has an answer.
  const settled = useRef(false);

  useEffect(() => {
    if (!ready || inFlight.current) return;

    // An existing session wins.
    if (isAuthed) { settled.current = true; return; }

    // Once answered, only a token the backend rejected reopens it. A sign-out
    // the user asked for must stay a sign-out — without this the Mine page's
    // sign-out button would put them straight back where they were.
    if (settled.current && lastSessionEndReason() !== 'unauthorized') return;
    if (attempts.current >= MAX_ATTEMPTS) { settled.current = true; return; }

    // Two sources, deliberately trusted differently.
    //
    // The live SDK read is self-proving: only a real Telegram client ever
    // populates `WebApp.initData`. A regular browser and an installed PWA both
    // load the SDK script from index.html, but it leaves the field empty
    // there. This path is left unguarded so a native open can't be blocked by
    // a mis-detection.
    //
    // The context value may instead come from the `tgWebAppData` hash that
    // main.tsx caches into sessionStorage — how Telegram Web AND Telegram
    // Desktop both deliver it. That is just a URL parameter, so it is only
    // honoured once TwaProvider has confirmed a Mini App session and the blob
    // is not a leftover from an earlier navigation in this tab.
    //
    // Provenance decides that, not `display-mode`. Desktop runs in a
    // chromeless app window and reports `standalone` exactly as an installed
    // PWA does, so the display-mode test threw away the only initData Desktop
    // ever had and no sign-in request was made at all. A payload this document
    // was launched with is fresh by construction; the PWA check still covers
    // the case it was written for, where storage holds a blob no URL supplied.
    const liveInitData = getLiveInitData();
    const freshFromLaunchUrl = hasTelegramLaunchPayload();
    const cachedUsable = isTelegram && (freshFromLaunchUrl || !isInstalledPwa());
    const cachedInitData = cachedUsable ? initData : null;
    const signedInitData = liveInitData ?? cachedInitData;

    // No payload yet. While detection is still running that means "too early",
    // not "not Telegram", so leave the question open for a later tick.
    if (!signedInitData) {
      if (!detecting) settled.current = true;
      return;
    }

    // Only the cold open holds the UI back. A recovery happens underneath a
    // screen the user is already reading, so blanking it for a spinner would
    // make a blip look worse than the logged-out flash it replaces.
    const isColdOpen = attempts.current === 0;
    attempts.current += 1;
    inFlight.current = true;
    if (isColdOpen) setPending(true);

    void (async () => {
      try {
        const r = await telegramMiniAppAuth({
          initData: signedInitData,
          invitation: getReferralCode() ?? undefined,
        });
        setSession(r.user, r.token, {
          source: 'telegram-miniapp',
          telegramId: user ? String(user.id) : null,
          blockedProviders: r.blockedProviders,
        });
        // Sign-in announcements: fetched here and nowhere else (see
        // loadLoginNotifications). Skipped on a recovery — the user saw them
        // when this session first opened and has not signed in again since.
        if (isColdOpen) void loadLoginNotifications();
      } catch (e) {
        // A refused handshake is an answer: stop, rather than spending the
        // remaining attempt on a backend that has already said no.
        settled.current = true;
        // Don't strand the user on a spinner — drop them on the logged-out UI
        // with the reason, where the manual sign-in paths still work.
        const message = mapAuthError(e, t);
        showError(message ?? (e instanceof Error ? e.message : t('login_failed')));
      } finally {
        inFlight.current = false;
        if (isColdOpen) setPending(false);
      }
    })();
  }, [ready, detecting, isAuthed, initData, isTelegram, user, getLiveInitData, setSession, showError, t]);

  return { pending };
}

/** Full-height placeholder shown while the automatic sign-in is in flight. */
export function AutoSignInLoader() {
  const t = useT('login');
  return (
    <div className="app-shell-h flex flex-col items-center justify-center gap-3 bg-surface">
      <Spinner containerClassName="" />
      <p className="text-[0.875rem] text-app/60">{t('signing_in')}</p>
    </div>
  );
}
